import { c as _c } from "react/compiler-runtime";
import { useTableState } from '../context/table-state-provider.tsx';
import { type CellData, type Column, type Row, type RowData, tableFeatures, useTable } from '../tanstack-table';

/** Where the Add column preview looks for examples beyond the rows on this page. */
export type TableColumnExamples<TData extends RowData = RowData> = {
  /** Names the rows in the line under a preview, e.g. "on 3 of 20 loads on this page". */
  itemLabel: {
    plural: string;
    singular: string;
  };
  /** More rows to look in when this page has too few with a value. */
  moreRows?: {
    isError?: boolean;
    isLoading: boolean;
    rows: readonly TData[];
  };
  /** Called as the Add column menu opens and closes, to load `moreRows` while it is open. */
  onMenuOpenChange?: (open: boolean) => void;
  /** Example values by column id, merged onto a real row when no row has a value. */
  values?: Readonly<Partial<Record<string, Partial<TData>>>>;
};
const MAX_EXAMPLES = 3;
const DEFAULT_ITEM_LABEL = {
  plural: 'rows',
  singular: 'row'
};
const isPresent = (value: CellData) => value !== null && value !== undefined && value !== '' && !(Array.isArray(value) && value.length === 0);
const hasColumnValue = <TData extends RowData,>(column: Column<TData, unknown>, row: Row<TData>) => column.columnDef.meta?.hasValue?.(row.original) ?? isPresent(row.getValue(column.id));

/** The line under a preview, saying where its examples came from. */
const describeSource = ({
  fromMoreRows,
  hasValue,
  isError,
  isExample,
  isLoading,
  itemLabel: {
    plural,
    singular
  },
  onPage,
  pageCount
}: {
  fromMoreRows: boolean;
  hasValue: boolean;
  isError: boolean;
  isExample: boolean;
  isLoading: boolean;
  itemLabel: TableColumnExamples['itemLabel'];
  onPage: number;
  pageCount: number;
}) => {
  if (isExample) {
    return isError ? `Example values: your latest ${plural} couldn't be loaded.` : `Example values: none of your latest ${plural} has one yet.`;
  }
  if (onPage > 0) {
    const page = `Shows a value on ${onPage} of ${pageCount} ${pageCount === 1 ? singular : plural} on this page`;
    return fromMoreRows ? `${page}; the rest are from your latest ${plural}.` : `${page}.`;
  }
  if (hasValue) return `From your latest ${plural}; none on this page has a value.`;
  if (isLoading) return `Looking for ${plural} with a value…`;
  if (isError) return `Your latest ${plural} couldn't be loaded.`;
  return `No ${plural} have a value here yet.`;
};

/**
 * Up to three rows with a value in `column`, as rows of a table over the same columns: rows on
 * this page first, then `examples.moreRows`, and last an example row, when no real row has one.
 */
export const useColumnExamples = (column, examples) => {
  const $ = _c(6);
  const {
    columns,
    table
  } = useTableState();
  const getRowId = table.options.getRowId;
  const pageRows = table.getRowModel().rows.map(_temp);
  const pageIds = new Set(getRowId ? pageRows.map((row_0, index) => getRowId(row_0, index)) : []);
  const moreRows = (examples?.moreRows?.rows ?? []).filter((row_1, index_0) => !getRowId || !pageIds.has(getRowId(row_1, index_0)));
  const isLoading = examples?.moreRows?.isLoading ?? false;
  const candidates = [...pageRows, ...moreRows];
  const exampleValues = examples?.values?.[column.id];
  const example = candidates[0] && exampleValues ? {
    ...candidates[0],
    ...exampleValues
  } : null;
  const previewTable = useTable({
    columns,
    data: example ? [...candidates, example] : candidates,
    features: tableFeatures
  });
  const rows = previewTable.getRowModel().rows;
  let t0;
  if ($[0] !== column) {
    t0 = row_2 => hasColumnValue(column, row_2);
    $[0] = column;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const withValue = rows.slice(0, candidates.length).filter(t0);
  const exampleRow = withValue.length === 0 && !isLoading ? rows.slice(candidates.length).find(row_3 => hasColumnValue(column, row_3)) : undefined;
  const shown = exampleRow ? [exampleRow] : withValue.slice(0, MAX_EXAMPLES);
  const t1 = Boolean(exampleRow);
  const t2 = describeSource({
    fromMoreRows: shown.some(row_4 => row_4.index >= pageRows.length),
    hasValue: withValue.length > 0,
    isError: examples?.moreRows?.isError ?? false,
    isExample: Boolean(exampleRow),
    isLoading,
    itemLabel: examples?.itemLabel ?? DEFAULT_ITEM_LABEL,
    onPage: withValue.filter(row_5 => row_5.index < pageRows.length).length,
    pageCount: pageRows.length
  });
  let t3;
  if ($[2] !== shown || $[3] !== t1 || $[4] !== t2) {
    t3 = {
      isExample: t1,
      rows: shown,
      source: t2
    };
    $[2] = shown;
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
};
function _temp(row) {
  return row.original;
}